import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { WidgetEditorDataService } from 'app/campaign/widget-editor-data.service';
import { WidgetService } from 'app/shared/services/widget.service';
import { SnackBarService } from 'app/core/snack-bar.service';
import {
  AddCollectionEvent,
  CurrentlySelectedCollection,
  MediaModel,
  MediaUpdateType,
  MediaCategory,
  MediaCollection,
} from '../media-library.model';
import { FilterNameImg } from 'app/shared/pipes/filter-string.pipe';
import { CurrentSiteIdStore } from 'app/core/current-site-id.store';
import { MatExpansionPanel } from '@angular/material/expansion';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from 'app/shared/confirmation-dialog/confirmation-dialog.component';
import { ConfirmationDialogData } from 'app/shared/confirmation-dialog/confirmation-dialog-config';
import { ComponentDescriptionGroup, WidgetComponentSubType, WidgetComponentType } from 'app/campaign/widgets.model';
import { DragService } from 'app/campaign/widget-editor/drag.service';
import { map, Observable, Subscription } from 'rxjs';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { ThemeService } from '../../services/theme.service';
import { MediaLibraryService } from '../media-library.service';

@Component({
  selector: 'app-media-picker',
  templateUrl: './media-picker.component.html',
  styleUrls: ['./media-picker.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class MediaPickerComponent implements OnInit, OnDestroy {
  private _searchString: string;
  public mediaFiles: Array<MediaModel>;
  public loading: boolean = true;
  @Input()
  set searchString(val: string) {
    this._searchString = val;
    this.collectionView = 'all';
    this.filterImages();
  }
  get searchString() {
    return this._searchString;
  }
  @Input() category: MediaCategory;
  @Input() default: MediaCategory;
  @Input() selectingMediaFile: boolean;
  @Input() supportsVideos: boolean = false;
  @Output() onSelect = new EventEmitter<any>();
  @Output() addCollection = new EventEmitter<AddCollectionEvent>();
  @Output() collectionSelected = new EventEmitter<CurrentlySelectedCollection>();
  @ViewChild('panel') panel: MatExpansionPanel;
  @ViewChild('nameInput') nameInput;
  @ViewChild('moveMenu') moveMenu;
  @ViewChild('categoryMenu') categoryMenu;
  @ViewChild('child', { read: ElementRef, static: false }) child: ElementRef;

  selectedMediaFiles: { [x: string]: MediaModel } = {};
  numSelected = 0;
  MediaCategory = MediaCategory;
  title: string;
  opened = false;
  actionLoading = false;
  categoryMediaFiles: Array<MediaModel>;
  collectionMediaFiles: Array<MediaModel>;
  filteredMediaFiles: Array<MediaModel>;
  getParentConnectorsSub: Subscription;
  connectedParent: string[];

  enteringCollectionName = false;
  moveCategory: MediaCategory;
  otherCategories: Array<MediaCategory>;
  otherCollections: { [x: string]: Array<MediaCollection> } = {};
  collectionView?: string = 'all';
  collectionName: string;
  collections: Array<MediaCollection> = null;
  userCollections: Array<MediaCollection> = [];
  filteredUserCollections: Array<MediaCollection> = [];
  triggerbeeCollections: Array<MediaCollection> = [];
  filteredTriggerbeeCollections: Array<MediaCollection> = [];
  darkMode: boolean = false;
  subscriptions: Array<Subscription> = [];

  constructor(
    private dialog: MatDialog,
    private widgetService: WidgetService,
    private snackbarService: SnackBarService,
    private widgetEditorDataService: WidgetEditorDataService,
    private currentSiteIdStore: CurrentSiteIdStore,
    private dragService: DragService,
    private themeService: ThemeService,
    private mediaLibraryService: MediaLibraryService,
  ) {}

  ngOnInit() {
    this.title = 'campaign.widget.sidebar.media.' + this.category;
    if (this.supportsVideos) {
      this.otherCategories = Object.values(MediaCategory).filter(
        (x) => x !== MediaCategory.Icons && x !== MediaCategory.Photos && x !== MediaCategory.Graphics,
      );
    } else {
      this.otherCategories = Object.values(MediaCategory).filter(
        (x) => x !== MediaCategory.Icons && x !== MediaCategory.Videos,
      );
    }
    this.subscriptions.push(
      this.themeService.isDarkThemeBS.subscribe((isDarkMode) => {
        this.darkMode = isDarkMode;
      }),
    );
    this.subscriptions.push(
      this.mediaLibraryService.collectionsBS.subscribe((collections) => {
        if (collections) {
          this.collections = collections[this.category];
          this.otherCollections = structuredClone(collections);

          this.userCollections = this.collections.filter((x) => !x.isTriggerbee);
          this.triggerbeeCollections = this.collections.filter((x) => x.isTriggerbee);

          this.otherCollections[MediaCategory.Photos] = this.otherCollections[MediaCategory.Photos].filter(
            (x) => !x.isTriggerbee,
          );
          this.otherCollections[MediaCategory.Graphics] = this.otherCollections[MediaCategory.Graphics].filter(
            (x) => !x.isTriggerbee,
          );
          this.filterCollections();
          this.loading = false;
        }
      }),
    );
    this.subscriptions.push(
      this.mediaLibraryService.mediaFilesBS.subscribe((imagesUpdate) => {
        if (
          imagesUpdate &&
          imagesUpdate.mediaFiles &&
          ((!this.mediaFiles && imagesUpdate.reason === MediaUpdateType.FirstFetch) ||
            imagesUpdate.reason !== MediaUpdateType.FirstFetch)
        ) {
          this.mediaFiles = imagesUpdate.mediaFiles.filter((x) => x.category == this.category);
          this.filterImages();
          this.filteredMediaFiles = this.collectionMediaFiles;
          this.tryOpenPanel();
        }
      }),
    );
  }

  private tryOpenPanel() {
    if (this.default == this.category && this.panel) {
      this.panel.open();
    } else if (this.default == this.category && !this.panel) {
      setTimeout(() => {
        this.panel.open();
      }, 10);
    }
  }

  createImageComponentDescription(image: MediaModel) {
    return {
      title: 'campaign.widget.component.image.title',
      icon: 'image',
      type: WidgetComponentType.Image,
      subType: WidgetComponentSubType.ImageRegular,
      isEnabled: true,
      isAdminOnly: false,
      isNew: false,
      imageData: image,
    };
  }

  private filterImages() {
    this.filterThisCategory();
    this.filterThisCollection();
    this.filterCollections();
    this.filterBySearch();
  }

  private filterThisCategory() {
    this.categoryMediaFiles = this.mediaFiles?.filter((x) => x.category == this.category);
  }

  private filterThisCollection() {
    if (this.collectionView == 'all' && (!this._searchString || this._searchString.length === 0)) {
      if (this.categoryMediaFiles) {
        this.collectionMediaFiles = this.categoryMediaFiles.filter(
          (x) => !x.uri.includes('triggerbee/images') && !x.uri.includes('triggerbee/videos'),
        );
      }
      return;
    } else if (this.collectionView == 'all' && this._searchString && this._searchString.length > 0) {
      if (this.categoryMediaFiles) {
        this.collectionMediaFiles = this.categoryMediaFiles;
      }
      return;
    }
    this.collectionMediaFiles = this.categoryMediaFiles.filter(
      (x) => x.collection && x.collection.name == this.collectionView,
    );
  }

  private filterCollections() {
    if (this._searchString && this._searchString.length > 0) {
      this.filteredUserCollections = this.userCollections.filter((x) =>
        x.name.toLowerCase().includes(this._searchString.toLowerCase()),
      );
      this.filteredTriggerbeeCollections = this.triggerbeeCollections.filter((x) =>
        x.name.toLowerCase().includes(this._searchString.toLowerCase()),
      );
    } else {
      this.filteredUserCollections = this.userCollections;
      this.filteredTriggerbeeCollections = this.triggerbeeCollections;
    }
  }

  private filterBySearch() {
    this.filteredMediaFiles = this.collectionMediaFiles
      ? new FilterNameImg().transform(this.collectionMediaFiles, this._searchString)
      : null;
  }

  toggleSelectedMediaFile(event, mediaFile: MediaModel) {
    event.stopPropagation();
    if (mediaFile.filename in this.selectedMediaFiles) {
      delete this.selectedMediaFiles[mediaFile.filename];
      this.numSelected--;
    } else {
      this.selectedMediaFiles[mediaFile.filename] = mediaFile;
      this.numSelected++;
    }
  }

  onCollectionSelected(collection: string) {
    this.collectionSelected.emit({
      collection: collection,
      category: this.category,
    } as CurrentlySelectedCollection);
    if (collection == this.collectionView) {
      this.collectionView = null;
    } else {
      this.collectionView = collection;
    }
    this.filterImages();
    if (this.collectionView && this.child) {
      setTimeout(() => {
        this.child.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    }
  }

  drop(event: CdkDragDrop<ComponentDescriptionGroup[]>) {
    this.dragService.drop(event);
  }

  onOpened() {
    this.collectionSelected.emit({ collection: 'all', category: this.category } as CurrentlySelectedCollection);
    this.opened = true;
    this.trySetChildConnectors();
    this.filterImages();
  }

  private trySetChildConnectors(count?: number) {
    setTimeout(() => {
      if (this.child) {
        this.dragService.setChildConnectors(this.child);
        this.getParentConnectorsSub = this.dragService.getParentConnectors().subscribe((data) => {
          this.connectedParent = data;
        });
      } else if (count && count < 5) {
        this.trySetChildConnectors(count + 1);
      }
    }, 10);
  }

  public moveMediaFilesBatch(event, category?: MediaCategory, collection?: MediaCollection) {
    event.stopPropagation();
    const doMoveImagesBatch = () => {
      const uris = Object.values(this.selectedMediaFiles).map((x: MediaModel) => x.uri);
      const serviceDestination =
        category === MediaCategory.Videos
          ? collection
            ? collection.name
            : ''
          : collection
            ? category + '/' + collection.name
            : category;
      const backendDestination =
        category === MediaCategory.Videos
          ? collection
            ? `/videos/${collection.name}/`
            : '/videos/'
          : collection
            ? `/images/${category}/${collection.name}/`
            : `/images/${category}/`;
      this.actionLoading = true;
      this.widgetService.moveMediaFiles(uris, backendDestination).subscribe(
        () => {
          const siteId = String(this.currentSiteIdStore.get());
          const mediaPath = category === MediaCategory.Videos ? '/videos/' : '/images/';
          const baseUrl = uris[0].split(siteId)[0] + siteId + mediaPath;
          const imageNames = Object.values(this.selectedMediaFiles).map((x) => x.filename);
          this.selectedMediaFiles = {};
          this.numSelected = 0;
          this.mediaLibraryService.moveMediaFiles(baseUrl, imageNames, serviceDestination, category, collection);
          this.widgetEditorDataService.updateInProgressBS.next({
            messages: 'generic.success',
          });
          this.actionLoading = false;
        },
        (error) => {
          this.snackbarService.error('generic.error');
          console.error(error);
          this.actionLoading = false;
        },
      );
    };
    if (window.localStorage.getItem('dontAsk_moveImages') === 'true') {
      doMoveImagesBatch();
      return;
    }

    const baseKey = this.supportsVideos
      ? 'campaign.widget.component.image.confirm-move-media'
      : 'campaign.widget.component.image.confirm-move-img';
    const isMultiple = Object.values(this.selectedMediaFiles).length > 1;
    const translationKey = isMultiple ? `${baseKey}.text-multiple` : `${baseKey}.text`;
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      maxWidth: '600px',
      minWidth: '300px',
      panelClass: 'confirm-dialog-panel',
      data: {
        text: translationKey,
        proceedButtonText: 'campaign.widget.component.image.confirm-move-img.btn-confirm',
        cancelButtonText: 'campaign.widget.component.image.confirm-move-img.btn-cancel',
        showDontAskAgain: true,
      } as ConfirmationDialogData,
    });
    dialogRef.afterClosed().subscribe((resultConfirm) => {
      if (!resultConfirm) {
        return;
      }
      if (resultConfirm.dontAskAgain) {
        window.localStorage.setItem('dontAsk_moveImages', 'true');
      }
      doMoveImagesBatch();
    });
  }

  public deleteMediaBatch(event) {
    event.stopPropagation();

    const baseKey = this.supportsVideos
      ? 'campaign.widget.component.image.confirm-delete-media'
      : 'campaign.widget.component.image.confirm-delete-img';
    const isMultiple = Object.values(this.selectedMediaFiles).length > 1;
    const translationKey = isMultiple ? `${baseKey}.text-multiple` : `${baseKey}.text`;
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      maxWidth: '600px',
      minWidth: '300px',
      panelClass: 'confirm-dialog-panel',
      data: {
        text: translationKey,
        proceedButtonText: 'campaign.widget.component.image.confirm-delete-img.btn-confirm',
        cancelButtonText: 'campaign.widget.component.image.confirm-delete-img.btn-cancel',
      },
    });

    dialogRef.afterClosed().subscribe((resultConfirm) => {
      if (!resultConfirm) {
        return;
      }
      const urls = Object.values(this.selectedMediaFiles).map((x: MediaModel) => x.url);
      const container = this.category === MediaCategory.Videos ? '/videos/' : '/images/';
      const mediaFileNames = urls.map((x: string) => {
        const i = x.indexOf(container);
        // New images with potential collections
        if (i != -1) return x.substring(i + container.length);
        // Legacy images with no collections
        else return x.substring(x.lastIndexOf('/') + 1);
      });
      this.actionLoading = true;
      this.widgetService.deleteMedia(mediaFileNames).subscribe(
        () => {
          const imageNames = Object.values(this.selectedMediaFiles).map((x) => x.filename);
          this.selectedMediaFiles = {};
          this.numSelected = 0;
          this.mediaLibraryService.removeMediaFiles(imageNames);
          this.widgetEditorDataService.updateInProgressBS.next({
            messages: 'campaign.widget.component.image.messages.delete-success',
          });
          this.actionLoading = false;
          this.filterThisCategory();
          this.filterThisCollection();
          this.filterBySearch();
        },
        (error) => {
          this.snackbarService.error('campaign.widget.component.image.messages.delete-failure');
          console.error(error);
          this.actionLoading = false;
        },
      );
    });
  }

  selectMediaFile(mediaFile: MediaModel) {
    this.onSelect.emit(mediaFile);
  }

  onAddCollectionClick() {
    if (!this.enteringCollectionName) {
      this.enteringCollectionName = true;
      setTimeout(() => {
        this.nameInput.nativeElement.focus();
      }, 50);
    } else {
      this.createCollection();
    }
  }

  createCollection() {
    if (this.collectionName && this.collectionName.trim().length > 0) {
      this.enteringCollectionName = false;
      this.collectionName = this.collectionName.trim().replace(/[^a-zA-Z0-9._-]/g, '_');
      if (!this.collections.some((x) => x.name === this.collectionName)) {
        const newCollection = { name: this.collectionName, isTriggerbee: false } as MediaCollection;
        const addCollectionEvent = {
          category: this.category,
          collection: newCollection,
        } as AddCollectionEvent;
        this.addCollection.emit(addCollectionEvent);
        this.collectionName = '';
      }
    } else {
      this.enteringCollectionName = false;
      this.collectionName = '';
    }
  }

  sanitizedCollectionName(name: string) {
    return name.replace(/_/g, ' ');
  }

  openMoveOptions(event) {
    event.stopPropagation();
    if (!this.collections || this.collections.length === 0) {
      this.categoryMenu.openMenu();
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((x) => x.unsubscribe());
    if (this.getParentConnectorsSub) {
      this.getParentConnectorsSub.unsubscribe();
    }
  }
}
